import { createContext, useContext, type ReactNode } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { ThemePref } from '../lib/theme';

type ThemeState = readonly [ThemePref, (p: ThemePref) => void];
const ThemeContext = createContext<ThemeState | null>(null);

/** Shares the signed-in person's theme (from useAccountTheme) with every switch in the layout. */
export function ThemeProvider({ value, children }: { value: ThemeState; children: ReactNode }) {
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

const OPTIONS: { value: ThemePref; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'Match device', icon: Monitor },
];

/** Light / Dark / Match device. Saved on your account, so it follows you to any device. */
export function ThemeSwitch({ compact = false }: { compact?: boolean }) {
  const state = useContext(ThemeContext);
  if (!state) throw new Error('ThemeSwitch needs a ThemeProvider');
  const [pref, setPref] = state;
  return (
    <div role="radiogroup" aria-label="Appearance" className={`grid grid-cols-3 gap-1 rounded-md bg-sunken p-1 ${compact ? 'w-[108px]' : ''}`}>
      {OPTIONS.map((o) => (
        <button
          key={o.value}
          role="radio"
          aria-checked={pref === o.value}
          title={o.label}
          onClick={() => setPref(o.value)}
          className={`flex items-center justify-center rounded ${compact ? 'min-h-7 py-1' : 'min-h-9 py-1.5'} ${pref === o.value ? 'bg-surface text-ink' : 'text-muted hover:text-ink'}`}
        >
          <o.icon className="size-4" aria-hidden />
          <span className="sr-only">{o.label}</span>
        </button>
      ))}
    </div>
  );
}
